import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Plus, FileText, Upload, FileSignature, Loader2, FolderLock, Copy, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { V5Shell, SegmentedControl, IOSButton, Field, StatusBadge, InsetList, Sheet } from "@/components/ios";
import ContractDetail from "@/components/legal/ContractDetail";
import InvoicePanel from "@/components/legal/InvoicePanel";
import { LoadingMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ClientPortalSheet({ open, onClose, token }) {
  const [clients, setClients] = useState(null);
  const [busyEmail, setBusyEmail] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  useEffect(() => {
    if (!open) return;
    setClients(null);
    axios
      .get(`${API}/legal/clients`, auth)
      .then((res) => setClients(res.data.clients))
      .catch(() => toast.error("Could not load clients"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copyText = (text, label) => {
    const done = () => toast.success(label);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => window.prompt("Copy portal link", text));
    } else {
      window.prompt("Copy portal link", text);
    }
  };

  const copyLink = async (email) => {
    setBusyEmail(email);
    try {
      const res = await axios.post(`${API}/legal/portal-link`, { client_email: email, origin_url: window.location.origin }, auth);
      copyText(res.data.url, "Portal link copied");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create portal link");
    } finally {
      setBusyEmail(null);
    }
  };

  const emailLink = async (email) => {
    setBusyEmail(email);
    try {
      await axios.post(`${API}/legal/portal-link/send`, { client_email: email, origin_url: window.location.origin }, auth);
      toast.success(`Portal link emailed to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email portal link");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Client portal links" testid="portal-sheet">
      <p className="text-[13px] text-[#3C3C43]/55 mb-4">
        A secure 30-day link per client — signed contracts to download and invoices to pay, no account needed.
      </p>
      {!clients && (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
        </div>
      )}
      {clients && clients.length === 0 && (
        <p className="text-[15px] text-[#3C3C43]/60 text-center py-8" data-testid="portal-clients-empty">
          No clients yet — create a contract or invoice with a client email first.
        </p>
      )}
      {clients && clients.length > 0 && (
        <InsetList testid="portal-client-list">
          {clients.map((c, i) => (
            <div key={c.email} className="flex items-center gap-3 px-5 py-4 min-h-[64px]" data-testid={`portal-client-row-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-[#0A0A0A] truncate">{c.name || c.email}</p>
                <p className="text-[13px] text-[#3C3C43]/55 truncate">{c.email}</p>
              </div>
              <button
                type="button"
                data-testid={`portal-client-copy-${i}`}
                onClick={() => copyLink(c.email)}
                disabled={busyEmail === c.email}
                aria-label={`Copy portal link for ${c.email}`}
                className="w-9 h-9 rounded-full bg-black/[0.05] text-[#002B5B] flex items-center justify-center active:scale-95 transition-transform duration-150 disabled:opacity-40"
              >
                <Copy className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                data-testid={`portal-client-email-${i}`}
                onClick={() => emailLink(c.email)}
                disabled={busyEmail === c.email}
                aria-label={`Email portal link to ${c.email}`}
                className="w-9 h-9 rounded-full bg-black/[0.05] text-[#002B5B] flex items-center justify-center active:scale-95 transition-transform duration-150 disabled:opacity-40"
              >
                {busyEmail === c.email ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
          ))}
        </InsetList>
      )}
    </Sheet>
  );
}

function NewContractSheet({ open, onClose, templates, onCreated, token }) {
  const [mode, setMode] = useState("template");
  const [template, setTemplate] = useState("mutual_nda");
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const create = async () => {
    setBusy(true);
    try {
      let res;
      if (mode === "upload") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title);
        fd.append("client_name", clientName);
        fd.append("client_email", clientEmail);
        res = await axios.post(`${API}/legal/contracts/upload`, fd, auth);
      } else {
        res = await axios.post(
          `${API}/legal/contracts`,
          { title, client_name: clientName, client_email: clientEmail, template },
          auth
        );
      }
      toast.success("Contract created");
      setTitle("");
      setClientName("");
      setClientEmail("");
      setFile(null);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create contract");
    } finally {
      setBusy(false);
    }
  };

  const valid = title.trim().length > 0 && (mode === "template" || file);

  return (
    <Sheet open={open} onClose={onClose} title="New contract" testid="contract-create-sheet">
      <div className="space-y-4">
        <SegmentedControl
          testid="contract-source"
          value={mode}
          onChange={setMode}
          options={[
            { value: "template", label: "Template" },
            { value: "upload", label: "Upload PDF" },
          ]}
        />
        {mode === "template" && (
          <div className="grid grid-cols-1 gap-2" data-testid="template-picker">
            {(templates || []).map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`template-${t.key}`}
                onClick={() => setTemplate(t.key)}
                className={`flex items-center gap-3 rounded-full border p-4 text-left transition-[border-color,background-color] duration-150 active:scale-[0.98] ${
                  template === t.key ? "border-[#002B5B] bg-[#002B5B]/[0.04]" : "border-black/[0.06] bg-white"
                }`}
              >
                <FileText className="w-5 h-5 text-[#002B5B] shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-[15px] font-semibold">{t.label}</p>
                  <p className="text-[13px] text-[#3C3C43]/55">Pre-drafted, fully editable before signing</p>
                </div>
              </button>
            ))}
          </div>
        )}
        {mode === "upload" && (
          <label
            data-testid="contract-upload-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-black/10 bg-white px-6 py-10 cursor-pointer active:scale-[0.99] transition-transform duration-150"
          >
            <Upload className="w-6 h-6 text-[#002B5B]" strokeWidth={1.5} />
            <span className="text-[15px] font-medium text-[#0A0A0A]">{file ? file.name : "Choose a PDF to sign"}</span>
            <span className="text-[13px] text-[#3C3C43]/50">Up to 4MB · 60 pages max</span>
            <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="contract-upload-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        )}
        <Field label="Document title" testid="new-contract-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Acme Corp NDA" maxLength={140} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Client name" testid="new-contract-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Corp" maxLength={120} />
          <Field label="Client email" testid="new-contract-client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="legal@acme.com" maxLength={120} />
        </div>
        <IOSButton data-testid="contract-create-submit" onClick={create} disabled={!valid || busy} className="w-full">
          {busy ? "Creating…" : "Create contract"}
        </IOSButton>
      </div>
    </Sheet>
  );
}

export default function AdminLegal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contracts");
  const [contracts, setContracts] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const token = localStorage.getItem("doclyx_token");

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  const loadContracts = () =>
    axios
      .get(`${API}/legal/contracts`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setContracts(res.data.contracts))
      .catch(() => toast.error("Could not load contracts"));

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    loadContracts();
    axios
      .get(`${API}/legal/templates`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setTemplates(res.data.templates))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <main className="bg-[#F2F2F7] min-h-screen flex items-center justify-center">
        <LoadingMark label="Loading legal workspace" />
      </main>
    );
  }

  return (
    <V5Shell title="Legal workspace" subtitle="Command center" testid="legal-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <SegmentedControl
          testid="legal-tabs"
          value={tab}
          onChange={setTab}
          options={[
            { value: "contracts", label: "Contracts" },
            { value: "invoices", label: "Invoices" },
          ]}
        />
        <div className="flex items-center gap-3">
          <IOSButton variant="plain" data-testid="client-portal-button" onClick={() => setPortalOpen(true)} className="whitespace-nowrap">
            <FolderLock className="w-4 h-4" strokeWidth={1.5} /> Client portal
          </IOSButton>
          {tab === "contracts" && (
            <IOSButton data-testid="new-contract-button" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4" strokeWidth={2} /> New contract
            </IOSButton>
          )}
        </div>
      </div>

      {tab === "contracts" && (
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
          <div className={selectedId ? "hidden lg:block" : ""}>
            {!contracts && (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
              </div>
            )}
            {contracts && contracts.length === 0 && (
              <div className="bg-white rounded-sm p-10 text-center" data-testid="contracts-empty">
                <FileSignature className="w-8 h-8 text-[#3C3C43]/25 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[15px] text-[#3C3C43]/60">No contracts yet. Start from a template or upload a PDF.</p>
              </div>
            )}
            {contracts && contracts.length > 0 && (
              <InsetList testid="contract-list">
                {contracts.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`contract-row-${i}`}
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full flex items-center gap-3 px-5 py-4 text-left min-h-[64px] transition-colors duration-150 active:bg-black/[0.03] ${
                      selectedId === c.id ? "bg-[#002B5B]/[0.05]" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-[#0A0A0A] truncate">{c.title}</p>
                      <p className="text-[13px] text-[#3C3C43]/55 truncate">
                        {c.kind === "upload" ? "Uploaded PDF" : "Template"} · {c.client_name || "No client"}
                      </p>
                    </div>
                    <StatusBadge status={c.status} />
                  </button>
                ))}
              </InsetList>
            )}
          </div>
          <div className={selectedId ? "" : "hidden lg:block"}>
            {selectedId ? (
              <ContractDetail
                contractId={selectedId}
                token={token}
                onChanged={loadContracts}
                onDeleted={(id) => {
                  setSelectedId(null);
                  loadContracts();
                }}
                onBack={() => setSelectedId(null)}
              />
            ) : (
              <div className="bg-white rounded-sm p-16 text-center shadow-[0_2px_10px_rgba(0,0,0,0.02),0_10px_30px_rgba(0,0,0,0.04)]" data-testid="contract-detail-empty">
                <FileSignature className="w-10 h-10 text-[#3C3C43]/20 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[15px] text-[#3C3C43]/55">Select a contract to edit, place signature fields, and sign.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "invoices" && <InvoicePanel token={token} />}

      <NewContractSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        templates={templates}
        token={token}
        onCreated={(c) => {
          setCreateOpen(false);
          setSelectedId(c.id);
          loadContracts();
        }}
      />
      <ClientPortalSheet open={portalOpen} onClose={() => setPortalOpen(false)} token={token} />
    </V5Shell>
  );
}
